import { DomainError, NetworkError, RequestTimeout } from 'scripts/application/lib/error';
import IdGenerator from 'scripts/domain/contracts/id_generator';
import handleTeamAssistV2Error from 'actions/team_assist_v2/lib/handle_team_assist_v2_error';
import { finishRequest, startRequest } from 'actions/team_assist_v2/lib/in_flight_team_assist_v2_requests';
import isTeamAssistV2Enabled from 'actions/team_assist_v2/lib/is_team_assist_v2_enabled';
import StreamingTeamAssistV2Turn from 'actions/team_assist_v2/lib/streaming_team_assist_v2_turn';
import { TeamAssistV2ErrorCode } from 'actions/team_assist_v2/lib/team_assist_v2_answer';

// glados refuses a longer question or displayedMessage.
export const TEAM_ASSIST_V2_QUESTION_MAX_LENGTH = 4000;

export default class AskTeamAssistV2 {
  constructor(context) {
    this.context = context;
  }

  run({ customerId, conversationId, question, displayedMessage, isRetry = false }) {
    if (!isTeamAssistV2Enabled(this.context.stores.appFeatures.get())) {
      return;
    }
    if (!isAskable(question) || !isWithinLimit(displayedMessage)) {
      return;
    }
    if (!customerId || !this.context.stores.customers.has({ id: customerId })) {
      return;
    }
    const store = this.context.stores.customers.storesFor(customerId).teamAssistsV2;
    if (isHoldingQuestions(store)) {
      return;
    }
    // A retry asks the same question again, which V1 doesn't count either.
    if (!isRetry) {
      this.context.analytics.track('Team Assist Question Asked', { customerId, teamAssistVersion: 'v2' });
    }

    const controller = startRequest(customerId);
    const turn = new StreamingTeamAssistV2Turn({
      store,
      signal: controller.signal,
      id: IdGenerator.newId(),
      question,
      displayedMessage,
      conversationId,
    });

    return this.context.gateways.teamAssistV2
      .streamTurn({
        body: turnBody({ turnId: turn.id, customerId, conversationId, question, displayedMessage }),
        signal: controller.signal,
        onEvent: event => {
          turn.apply(event);
          this.trackIfSuggestedReplyShown(customerId, turn, event);
        },
      })
      .then(() => this.onStreamClosed(turn))
      .catch(err => this.onStreamFailed(turn, controller.signal, err))
      .finally(() => finishRequest(customerId, controller));
  }

  // Once per answer, when it finishes with a draft, as V1 does when a
  // suggested response arrives.
  trackIfSuggestedReplyShown(customerId, turn, event) {
    if (event.type !== 'done' || !turn.answer.blocks.some(block => block.type === 'suggested_reply')) {
      return;
    }
    this.context.analytics.track('Team Assist Suggested Response Shown', {
      customerId,
      messageId: turn.answer.messageId,
      teamAssistVersion: 'v2',
    });
  }

  onStreamClosed(turn) {
    if (turn.live) {
      turn.fail(TeamAssistV2ErrorCode.interrupted);
    }
  }

  onStreamFailed(turn, signal, err) {
    if (signal.aborted || !turn.live) {
      return;
    }
    turn.fail(failureCode(err));
    if (!isTurnConflict(err)) {
      handleTeamAssistV2Error(this.context, err);
    }
  }
}

/**
 * Whether the customer's turns can't take a question yet: until a Clear's
 * DELETE answers, glados would add it to the old session; until the session
 * loads, it would continue one whose earlier turns the agent can't see; and
 * one answer streams at a time.
 */
export function isHoldingQuestions(store) {
  return store.isClearing() || store.isLoading() || store.findAll().some(turn => turn.live);
}

function failureCode(err) {
  if (err instanceof RequestTimeout) {
    return TeamAssistV2ErrorCode.interrupted;
  }
  return err instanceof NetworkError ? TeamAssistV2ErrorCode.network : TeamAssistV2ErrorCode.requestFailed;
}

function isAskable(question) {
  return Boolean(question) && question.trim().length > 0 && isWithinLimit(question);
}

function isWithinLimit(text) {
  return !text || text.length <= TEAM_ASSIST_V2_QUESTION_MAX_LENGTH;
}

// A 409 means glados already has this turn, answered or still running.
function isTurnConflict(err) {
  return err instanceof DomainError && err.errors.some(e => TURN_CONFLICT_CODES.includes(e.code));
}

const TURN_CONFLICT_CODES = ['duplicate_turn', 'turn_in_progress'];

// glados keeps the session's history, so a turn sends only its own question.
function turnBody({ turnId, customerId, conversationId, question, displayedMessage }) {
  const body = { turnId, idempotencyKey: turnId, question, customerId };
  if (displayedMessage) {
    body.displayedMessage = displayedMessage;
  }
  if (conversationId) {
    body.currentConversationId = conversationId;
  }
  return body;
}
